import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Button from '../../../../../shared/_TBComponents/Button';
import Icon from '../../../../../shared/_TBComponents/Icon';
import Modal from '../../../../../shared/_TBComponents/Modal';
import Text from '../../../../../shared/_TBComponents/Text';
import { FOCUS_BLUE } from '../../../../../shared/theme/focusStyles';

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 1200px;
  max-width: 100%;
  box-sizing: border-box;
  padding: 64px;

  @media screen and (max-width: 767px) {
    padding: 30px;
  }
`;

const TileGroup = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 40px;
  padding: 64px 0;
  justify-content: center;
  align-items: stretch;

  @media screen and (max-width: 1280px) {
    padding: 20px 0;
  }

  @media screen and (max-width: 767px) {
    gap: 24px;
    padding: 20px 0;
  }
`;

const Tile = styled.a`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: center;
  flex: 1 1 0;
  min-width: 315px;
  height: 270px;
  background: white;
  border: 1px solid #EEE;
  border-radius: 8px;
  box-shadow: 0 16px 24px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05);
  padding: 48px 24px;
  gap: 8px;
  cursor: pointer;
  transition: box-shadow 0.2s, border-color 0.2s;
  text-decoration: none;
  color: inherit;
  font: inherit;

  /* Matches the hover look (minus its fade) — global.scss strips focus outlines app-wide. */
  &:focus-visible {
    box-shadow: 0 24px 32px 0 rgba(48, 49, 51, 0.15), 0 0 2px 0 rgba(48, 49, 51, 0.08);
    border-color: ${FOCUS_BLUE};
  }

  &:hover {
    box-shadow: 0 24px 32px 0 rgba(48, 49, 51, 0.15), 0 0 2px 0 rgba(48, 49, 51, 0.08);
    border-color: #77A7FF;
    opacity: 0.7;
  }

  @media screen and (max-width: 767px) {
    height: auto;
    min-height: 270px;
  }
`;

const AddOptionsModal = ({ isOpen, onClose, options }) => (
  <Modal
    isOpen={isOpen}
    onRequestClose={onClose}
    hideButton
    hideTitle
    customMaxWidth='min(1200px, 90vw)'
    customPadding='0px'
    customBorderRadius='8px'
    flexGrow={false}
    widthFitContent
    overlayBackgroundColor='rgba(0, 0, 0, 0.6)'
  >
    <ModalContent>
      <Text
        styledTextProps={{ fontSize: '28px', fontWeight: 700 }}
        styledWrapperProps={{ textAlign: 'center' }}
      >
        Add Athletes
      </Text>
      <Text
        styledTextProps={{ fontSize: '19px', color: '#1A1A1A' }}
      >
        Choose how you would like to create a new athlete.
      </Text>
      <TileGroup>
        {options.map((option) => (
          <Tile
            key={option.key}
            as={option.onClick ? 'button' : 'a'}
            type={option.onClick ? 'button' : undefined}
            href={option.onClick ? undefined : option.href}
            onClick={() => {
              if (option.onClick) option.onClick();
              else onClose();
            }}
          >
            <Icon
              icon={option.icon}
              size={64}
              color='#77A7FF'
              styledProps={{ marginBottom: '16px' }}
            />
            <Text styledTextProps={{ fontSize: '19px', fontWeight: 700 }}>
              {option.title}
            </Text>
            <Text styledTextProps={{ fontSize: '16px', color: '#1A1A1A' }}>
              {option.description}
            </Text>
          </Tile>
        ))}
      </TileGroup>
      <Button
        name='Cancel'
        onClick={onClose}
        fontSize='13px'
        txtColor='#9E9E9E'
        noBorder
        upperCase
      />
    </ModalContent>
  </Modal>
);

AddOptionsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    href: PropTypes.string,
    onClick: PropTypes.func,
    icon: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
  })).isRequired,
};

export default AddOptionsModal;
